/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-use-before-define */
import type { Matrix4 } from '../matrix/Matrix4';
import { Vector3 } from '../vector/Vector3';
import { Box3 } from './Box3';
import type { Plane } from './Plane';

export class Sphere {
  public center: Vector3;
  public radius: number;

  constructor(center?: Vector3, radius?: number) {
    this.center = center !== undefined ? center : new Vector3();
    this.radius = radius !== undefined ? radius : 0;
  }

  public set(center: Vector3, radius: number): this {
    this.center.copy(center);
    this.radius = radius;

    return this;
  }

  public setFromPoints(points: Vector3[], optionalCenter?: Vector3): this {
    const center = this.center;

    if (optionalCenter !== undefined) {
      center.copy(optionalCenter);
    } else {
      box.setFromPoints(points).getCenter(center);
    }

    let maxRadiusSq = 0;

    for (let i = 0, il = points.length; i < il; i++) {
      maxRadiusSq = Math.max(maxRadiusSq, center.distanceToSquared(points[i]));
    }

    this.radius = Math.sqrt(maxRadiusSq);

    return this;
  }

  public clone(): Sphere {
    return new Sphere().copy(this);
  }

  public copy(sphere: Sphere): this {
    this.center.copy(sphere.center);
    this.radius = sphere.radius;

    return this;
  }

  public empty(): boolean {
    return this.radius <= 0;
  }

  public containsPoint(point: Vector3): boolean {
    return point.distanceToSquared(this.center) <= this.radius * this.radius;
  }

  public distanceToPoint(point: Vector3): number {
    return point.distanceTo(this.center) - this.radius;
  }

  public intersectsSphere(sphere: Sphere): boolean {
    const radiusSum = this.radius + sphere.radius;

    return (
      sphere.center.distanceToSquared(this.center) <= radiusSum * radiusSum
    );
  }

  public intersectsBox(box: Box3): boolean {
    return box.intersectsSphere(this);
  }

  public intersectsPlane(plane: Plane): boolean {
    return Math.abs(plane.distanceToPoint(this.center)) <= this.radius;
  }

  public clampPoint(point: Vector3, out: Vector3): Vector3 {
    const deltaLengthSq = this.center.distanceToSquared(point);

    out.copy(point);

    if (deltaLengthSq > this.radius * this.radius) {
      out.sub(this.center).normalize();
      out.multiplyScalar(this.radius).add(this.center);
    }

    return out;
  }

  public getBoundingBox(out: Box3): Box3 {
    out.set(this.center, this.center);
    out.expandByScalar(this.radius);

    return out;
  }

  public applyMatrix4(matrix: Matrix4): this {
    this.center.applyMatrix4(matrix);
    this.radius = this.radius * matrix.getMaxScaleOnAxis();

    return this;
  }

  public translate(offset: Vector3): this {
    this.center.add(offset);

    return this;
  }

  public equals(sphere: Sphere): boolean {
    return sphere.center.equals(this.center) && sphere.radius === this.radius;
  }
}

const box = new Box3();
